import { BackendApiError } from './minecardsBackendClient'

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const codePattern = /^\d{6}$/

const normalizeEmail = (value) =>
    String(value || '')
        .trim()
        .toLowerCase()
const normalizeCode = (value) => String(value || '').replace(/\D/g, '')

const request = async (path, options = {}) => {
    const headers = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...options.headers,
    }
    const baseUrl = String(
        options.baseUrl ?? process.env.REACT_APP_BACKEND_BASE_URL ?? ''
    ).replace(/\/+$/, '')
    const fetchImpl = options.fetchImpl || window.fetch.bind(window)

    let response
    try {
        response = await fetchImpl(`${baseUrl}${path}`, {
            method: options.method || 'POST',
            headers,
            body: JSON.stringify(options.body),
            credentials: 'include',
        })
    } catch (error) {
        throw new BackendApiError('Backend request failed', {
            code: 'network_error',
            cause: error,
        })
    }

    const rawBody = await response.text()
    let payload = null
    if (rawBody) {
        try {
            payload = JSON.parse(rawBody)
        } catch (error) {
            throw new BackendApiError('Backend returned invalid JSON', {
                status: response.status,
                code: 'invalid_response',
                cause: error,
            })
        }
    }
    if (!response.ok) {
        const apiError = payload?.error || {}
        throw new BackendApiError(
            apiError.message || `Backend request failed (${response.status})`,
            {
                status: response.status,
                code: apiError.code || 'http_error',
                requestId: apiError.requestId,
            }
        )
    }
    return payload
}

const emailBody = (email, code) => {
    const normalizedEmail = normalizeEmail(email)
    if (!emailPattern.test(normalizedEmail) || normalizedEmail.length > 254) {
        throw new BackendApiError('Введите корректную почту', {
            code: 'invalid_email',
        })
    }
    const body = { email: normalizedEmail }
    if (code !== undefined) {
        const normalizedCode = normalizeCode(code)
        if (!codePattern.test(normalizedCode)) {
            throw new BackendApiError('Введите шестизначный код', {
                code: 'invalid_email_code',
            })
        }
        body.code = normalizedCode
    }
    return body
}

const authHeaders = async (user) => {
    if (!user || typeof user.getIdToken !== 'function') {
        return {}
    }
    return { Authorization: `Bearer ${await user.getIdToken()}` }
}

export const requestEmailLogin = async (email, options = {}) =>
    request('/api/v1/auth/email/challenges', {
        ...options,
        body: emailBody(email),
    })

export const verifyEmailLogin = async (email, code, options = {}) =>
    request('/api/v1/auth/email/verify', {
        ...options,
        body: emailBody(email, code),
    })

export const requestEmailLink = async (user, email, options = {}) =>
    request('/api/v1/me/email/challenges', {
        ...options,
        headers: { ...options.headers, ...(await authHeaders(user)) },
        body: emailBody(email),
    })

export const verifyEmailLink = async (user, email, code, options = {}) =>
    request('/api/v1/me/email/verify', {
        ...options,
        headers: { ...options.headers, ...(await authHeaders(user)) },
        body: emailBody(email, code),
    })

export const getEmailLink = async (user, options = {}) => {
    const headers = {
        Accept: 'application/json',
        ...(await authHeaders(user)),
        ...options.headers,
    }
    const baseUrl = String(
        options.baseUrl ?? process.env.REACT_APP_BACKEND_BASE_URL ?? ''
    ).replace(/\/+$/, '')
    const fetchImpl = options.fetchImpl || window.fetch.bind(window)
    const response = await fetchImpl(`${baseUrl}/api/v1/me/email`, {
        headers,
        credentials: 'include',
    })
    const payload = await response.json()
    if (!response.ok) {
        const apiError = payload?.error || {}
        throw new BackendApiError(
            apiError.message || 'Backend request failed',
            {
                status: response.status,
                code: apiError.code || 'http_error',
            }
        )
    }
    return payload
}

export const formatEmailCode = (value) => {
    const digits = normalizeCode(value).slice(0, 6)
    return digits.length > 3
        ? `${digits.slice(0, 3)} ${digits.slice(3)}`
        : digits
}
